import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const money = value => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const FAQ = [
  ['How do I calculate a basic fence estimate?', 'A simple starting formula is fence length multiplied by your per-foot rate, plus gate pricing, tear-out pricing and any other job-specific charges you choose to include.'],
  ['Should labor be included in the per-foot rate?', 'That depends on how you maintain your price book. Some contractors build labor and material into the per-foot selling rate, while others separate them. Consistency matters more than the format.'],
  ['Does this calculator calculate profit?', 'No. This calculator estimates a selling subtotal from your rates and quantities. Use Profit Guard afterward to compare the selling price with the direct job costs you enter.'],
  ['Does this create a PicketOps estimate?', 'No. This free tool does not save customer data or create records. Signed-in contractors can use the full Estimates workflow to save a customer quote.'],
];

export default function FenceEstimateCalculator() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [feet, setFeet] = useState(searchParams.get('feet') || '');
  const [rate, setRate] = useState('');
  const [gates, setGates] = useState(searchParams.get('gates') || '0');
  const [gatePrice, setGatePrice] = useState('');
  const [tearoutFeet, setTearoutFeet] = useState(searchParams.get('tearout') || '0');
  const [tearoutRate, setTearoutRate] = useState('');
  const [other, setOther] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [copied, setCopied] = useState(false);
  const [exampleLoaded, setExampleLoaded] = useState(false);

  const result = useMemo(() => {
    const values = {
      feet: Number(feet),
      rate: Number(rate),
      gates: Number(gates),
      gatePrice: Number(gatePrice),
      tearoutFeet: Number(tearoutFeet),
      tearoutRate: Number(tearoutRate),
      other: Number(other),
      taxRate: Number(taxRate),
    };
    const requiredReady = String(feet).trim() !== '' && String(rate).trim() !== '';
    const conditionalRatesReady = (values.gates === 0 || String(gatePrice).trim() !== '')
      && (values.tearoutFeet === 0 || String(tearoutRate).trim() !== '');
    const valid = requiredReady && conditionalRatesReady
      && Object.values(values).every(Number.isFinite)
      && values.feet >= 0 && values.rate >= 0
      && Number.isInteger(values.gates) && values.gates >= 0
      && values.gatePrice >= 0 && values.tearoutFeet >= 0
      && values.tearoutRate >= 0 && values.other >= 0
      && values.taxRate >= 0 && values.taxRate <= 100;

    if (!valid) return { valid: false, fence: 0, gate: 0, tearout: 0, subtotal: 0, tax: 0, total: 0 };

    const fence = values.feet * values.rate;
    const gate = values.gates * values.gatePrice;
    const tearout = values.tearoutFeet * values.tearoutRate;
    const subtotal = fence + gate + tearout + values.other;
    const tax = subtotal * (values.taxRate / 100);
    return { valid: true, fence, gate, tearout, subtotal, tax, total: subtotal + tax };
  }, [feet, rate, gates, gatePrice, tearoutFeet, tearoutRate, other, taxRate]);

  const reset = () => {
    setFeet('');
    setRate('');
    setGates('0');
    setGatePrice('');
    setTearoutFeet('0');
    setTearoutRate('');
    setOther('');
    setTaxRate('');
    setCopied(false);
    setExampleLoaded(false);
  };

  const loadExample = () => {
    setFeet('150');
    setRate('55');
    setGates('1');
    setGatePrice('475');
    setTearoutFeet('80');
    setTearoutRate('8');
    setOther('250');
    setTaxRate('0');
    setExampleLoaded(true);
  };

  const copyEstimate = async () => {
    if (!result.valid) return;
    const summary = [
      'PicketOps Fence Estimate Calculator',
      'Fence: ' + feet + ' ft × ' + money(rate) + '/ft = ' + money(result.fence),
      'Gates: ' + gates + ' × ' + money(gatePrice) + ' = ' + money(result.gate),
      'Tear-out: ' + tearoutFeet + ' ft × ' + money(tearoutRate) + '/ft = ' + money(result.tearout),
      'Other charges: ' + money(other),
      'Subtotal: ' + money(result.subtotal),
      'Tax: ' + money(result.tax),
      'Estimated total: ' + money(result.total),
      '',
      'Planning calculation only. Confirm scope, actual rates, taxes and job conditions before quoting.',
    ].join('\n');

    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const workspaceTarget = isAuthenticated ? '/estimates' : '/contractor/signup';
  const hasQuoteInput = String(feet).trim() !== ''
    || String(rate).trim() !== ''
    || String(gatePrice).trim() !== ''
    || String(tearoutRate).trim() !== ''
    || String(other).trim() !== ''
    || String(taxRate).trim() !== ''
    || String(gates) !== '0'
    || String(tearoutFeet) !== '0';

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <a href="#estimate-calculator" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-3 focus:text-primary-foreground">
        Skip to fence estimate calculator
      </a>

      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span>
            <span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
            <span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span>
          </span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Fence estimate calculator</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free fence estimate calculator · No signup required</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">
                Calculate a fence estimate from footage, gates and tear-out
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
                Test your own selling rates and job quantities, see the price breakdown instantly, then pass the total into Profit Guard to review gross margin.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><a href="#estimate-calculator">Calculate estimate <ApperIcon name="ArrowDown" size={16}/></a></Button>
                <Button variant="outline" asChild><Link to="/fence-estimate-checklist">Open estimate checklist</Link></Button>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Basic estimating formula</p>
              <div className="mt-5 space-y-3 text-sm">
                {[
                  'Fence footage × per-foot selling rate',
                  'Gate quantity × gate price',
                  'Tear-out footage × removal rate',
                  'Other job-specific charges',
                  'Optional tax calculation',
                ].map((step, index) => (
                  <div key={step} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold">{index + 1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">This estimates selling price only. It does not know your material, labor, overhead or profit costs unless you evaluate them separately.</p>
            </div>
          </div>
        </section>

        <section id="estimate-calculator" aria-labelledby="estimate-calculator-heading" className="scroll-mt-6 grid lg:grid-cols-[0.95fr_1.05fr] gap-5">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Quote inputs</p>
                <h2 id="estimate-calculator-heading" className="mt-2 text-2xl font-heading font-semibold">Use your own rates.</h2>
                <p className="mt-2 text-sm text-muted-foreground">Enter your own company rates. Or load a clearly labeled illustrative example to learn the workflow.</p>
                {exampleLoaded && <p className="mt-2 rounded-md border border-warning-border bg-warning-muted px-3 py-2 text-xs text-warning">Illustrative example loaded. These values are not market-price recommendations.</p>}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="estimate-feet">Fence length (ft)</Label><Input id="estimate-feet" type="number" min="0" step="0.01" inputMode="decimal" value={feet} onChange={event=>setFeet(event.target.value)} placeholder="150"/></div>
                <div className="space-y-2"><Label htmlFor="estimate-rate">Selling rate per ft</Label><Input id="estimate-rate" type="number" min="0" step="0.01" inputMode="decimal" value={rate} onChange={event=>setRate(event.target.value)} placeholder="55.00"/></div>
                <div className="space-y-2"><Label htmlFor="estimate-gates">Number of gates</Label><Input id="estimate-gates" type="number" min="0" step="1" inputMode="numeric" value={gates} onChange={event=>setGates(event.target.value)} placeholder="1"/></div>
                <div className="space-y-2"><Label htmlFor="estimate-gate-price">Price per gate</Label><Input id="estimate-gate-price" type="number" min="0" step="0.01" inputMode="decimal" value={gatePrice} onChange={event=>setGatePrice(event.target.value)} placeholder="475.00"/></div>
                <div className="space-y-2"><Label htmlFor="estimate-tearout-feet">Tear-out length (ft)</Label><Input id="estimate-tearout-feet" type="number" min="0" step="0.01" inputMode="decimal" value={tearoutFeet} onChange={event=>setTearoutFeet(event.target.value)} placeholder="0"/></div>
                <div className="space-y-2"><Label htmlFor="estimate-tearout-rate">Tear-out rate per ft</Label><Input id="estimate-tearout-rate" type="number" min="0" step="0.01" inputMode="decimal" value={tearoutRate} onChange={event=>setTearoutRate(event.target.value)} placeholder="8.00"/></div>
                <div className="space-y-2"><Label htmlFor="estimate-other">Other charges</Label><Input id="estimate-other" type="number" min="0" step="0.01" inputMode="decimal" value={other} onChange={event=>setOther(event.target.value)} placeholder="0.00"/></div>
                <div className="space-y-2"><Label htmlFor="estimate-tax">Tax rate (%)</Label><Input id="estimate-tax" type="number" min="0" max="100" step="0.01" inputMode="decimal" value={taxRate} onChange={event=>setTaxRate(event.target.value)} placeholder="0"/></div>
              </div>

              {hasQuoteInput && !result.valid && <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">Add fence length and a selling rate. If gates or tear-out are above zero, add their rate too. Use nonnegative numbers, whole gates, and a tax rate from 0 to 100.</p>}

              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={loadExample}><ApperIcon name="Sparkles" size={16}/>Load illustrative example</Button>
                <Button type="button" variant="ghost" onClick={reset}><ApperIcon name="RotateCcw" size={16}/>Clear calculator</Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-sm)">
              <div className="p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Estimate preview</p>
                <p className="mt-2 text-5xl font-bold tabular-nums tracking-tight">{result.valid ? money(result.total) : '—'}</p>
                <p className="mt-2 text-sm text-muted-foreground">Calculated selling total from the rates and quantities above.</p>

                <dl className="mt-6 divide-y divide-border rounded-xl border border-border bg-background px-4">
                  {[
                    ['Fence', result.fence],
                    ['Gates', result.gate],
                    ['Tear-out', result.tearout],
                    ['Other charges', Number(other) || 0],
                    ['Subtotal', result.subtotal],
                    ['Tax', result.tax],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-4 py-3 text-sm">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="font-semibold tabular-nums">{result.valid ? money(value) : '—'}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="border-t border-border bg-primary/5 p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Next revenue check</p>
                <h2 className="mt-2 text-xl font-heading font-semibold">Price is only half of the decision.</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Send this total into Profit Guard and compare it with your direct material, labor, equipment and other job costs.</p>
                {result.valid ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button asChild>
                      <Link to={'/fence-profit-margin-calculator?price=' + encodeURIComponent(result.total.toFixed(2)) + '&job=' + encodeURIComponent('Fence estimate calculator')}>
                        Run Profit Guard <ApperIcon name="ShieldCheck" size={16}/>
                      </Link>
                    </Button>
                    <Button variant="outline" asChild>
                      <Link to={'/fence-labor-crew-planner?feet=' + encodeURIComponent(feet) + '&price=' + encodeURIComponent(result.total.toFixed(2)) + '&job=' + encodeURIComponent('Fence estimate calculator')}>
                        Plan labor <ApperIcon name="Users" size={16}/>
                      </Link>
                    </Button>
                    <Button variant="outline" asChild>
                      <Link to={'/fence-estimate-template?feet=' + encodeURIComponent(feet) + '&gates=' + encodeURIComponent(gates) + '&tearout=' + encodeURIComponent(tearoutFeet)}>
                        Build scope <ApperIcon name="FilePenLine" size={16}/>
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <Button className="mt-4" disabled><ApperIcon name="ShieldCheck" size={16}/>Complete estimate inputs first</Button>
                )}
              </div>
            </section>

            <div className="flex flex-wrap gap-3">
              <Button type="button" onClick={copyEstimate} disabled={!result.valid}><ApperIcon name={copied?'Check':'Copy'} size={16}/>{copied?'Copied':'Copy estimate breakdown'}</Button>
              <Button variant="outline" asChild><Link to={workspaceTarget}>{isAuthenticated?'Build a saved estimate':'Create account'} <ApperIcon name="ArrowRight" size={16}/></Link></Button>
            </div>

            <p className="text-xs leading-relaxed text-muted-foreground">Planning tool only. Confirm measurements, actual company rates, tax treatment, scope, permits, site conditions and contract terms before presenting a customer price.</p>
          </div>
        </section>

        <section aria-labelledby="estimate-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="estimate-faq-heading" className="text-2xl font-heading font-semibold">Fence estimating questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">
            {FAQ.map(([question, answer]) => (
              <details key={question} className="py-4">
                <summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export const route = { path: '/fence-estimate-calculator', layout: 'public' };
